/**
 * Admin › Sync: what the Oracle poller, RoleSync and the nightly reconcile have pulled from
 * Fusion (spec §7). Polls /api/admin/sync every 30 s while the tab is open and flags any source
 * whose last success is more than an hour old (the spec's "alert if lag exceeds 1 hour").
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SYNC_LAG_WARN_MS } from '@erp-attach/shared';
import type { SyncCounts, SyncRun, SyncRunStatus, SyncSourceSummary } from '@erp-attach/shared';
import { useApi } from '../../api/ApiProvider';
import { useApiQuery } from '../../api/useApiQuery';
import { formatDateTime, formatRelative } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Chip } from '../../components/ui/Chip';
import type { ChipVariant } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { Icon } from '../../components/ui/Icon';
import { Spinner } from '../../components/ui/Spinner';

export const SYNC_POLL_MS = 30_000;
const RUNS_PAGE = 50;

const SOURCE_LABEL: Record<string, string> = {
  customers: 'Customers',
  sites: 'Sites / jobs',
  suppliers: 'Suppliers',
  'user-roles': 'User roles',
  reconcile: 'Nightly reconcile',
  'audit-export': 'Audit export',
};

const SOURCE_HINT: Record<string, string> = {
  customers: 'SOAP CustomerAccountService, every 15 min',
  sites: 'Sites and job DFFs from the same account pull',
  suppliers: 'REST suppliers, every 15 min',
  'user-roles': 'BIP user-role report, every 15 min',
  reconcile: 'Full keyset diff, nightly',
  'audit-export': 'Audit chain to immutable storage',
};

/** Expected cadence per source. Spec §7 says one hour; the nightly reconcile gets a day plus slack. */
const LAG_WARN_MS: Record<string, number> = { reconcile: 26 * 60 * 60 * 1000 };

const STATUS_META: Record<SyncRunStatus, { label: string; variant: ChipVariant }> = {
  running: { label: 'Running', variant: 'info' },
  success: { label: 'Success', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

const COUNT_TILES: { key: keyof SyncCounts; label: string; hint: string }[] = [
  { key: 'customers', label: 'Customers', hint: 'customer_account rows' },
  { key: 'sites', label: 'Sites / jobs', hint: 'customer_site rows' },
  { key: 'suppliers', label: 'Suppliers', hint: 'supplier rows' },
  { key: 'fusionUsers', label: 'Fusion users', hint: 'fusion_user rows' },
  { key: 'fusionRoles', label: 'Fusion roles', hint: 'fusion_role rows' },
  { key: 'activeMemberships', label: 'Active role memberships', hint: 'principal_role_membership where active' },
  { key: 'principals', label: 'Signed-in principals', hint: 'principal rows (anyone who has signed in)' },
  { key: 'principalsPinned', label: 'Pinned to Fusion', hint: 'principals linked to a Fusion user' },
  { key: 'roleMappings', label: 'Role mappings', hint: 'app_role_mapping rows' },
];

export function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] ?? source;
}

/** "0:42", "12 min", "3 h 05 min", "2 d 4 h" — short enough for a table cell. */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${String(m % 60).padStart(2, '0')} min`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} h`;
}

export interface Lag {
  /** now − lastSuccessAt; null when the source has never succeeded. */
  ms: number | null;
  warn: boolean;
}

export function lagOf(summary: SyncSourceSummary, now: number): Lag {
  if (!summary.lastSuccessAt) return { ms: null, warn: summary.lastRun !== null };
  const ms = Math.max(0, now - new Date(summary.lastSuccessAt).getTime());
  return { ms, warn: ms > (LAG_WARN_MS[summary.source] ?? SYNC_LAG_WARN_MS) };
}

function runDuration(run: SyncRun, now: number): number | null {
  const start = new Date(run.startedAt).getTime();
  const end = run.finishedAt ? new Date(run.finishedAt).getTime() : run.status === 'running' ? now : null;
  return end === null || Number.isNaN(start) ? null : end - start;
}

const num = (n: number) => n.toLocaleString();

function SourceCard({ summary, now }: { summary: SyncSourceSummary; now: number }) {
  const run = summary.lastRun;
  const lag = lagOf(summary, now);
  const status = run ? STATUS_META[run.status] : { label: 'No runs yet', variant: 'neutral' as ChipVariant };
  const lagChip: { label: string; variant: ChipVariant; title: string } = lag.ms === null
    ? lag.warn
      ? { label: 'Never succeeded', variant: 'warning', title: 'This source has run but has no successful run on record' }
      : { label: 'Waiting for first run', variant: 'neutral', title: 'No sync run has been recorded for this source yet' }
    : lag.warn
      ? { label: `Lag ${formatDuration(lag.ms)}`, variant: 'warning', title: `Last success ${formatDateTime(summary.lastSuccessAt)} — more than the expected cadence` }
      : { label: `Lag ${formatDuration(lag.ms)}`, variant: 'success', title: `Last success ${formatDateTime(summary.lastSuccessAt)}` };

  return (
    <article
      className={`gsl-panel flex flex-col gap-3 p-4 ${lag.warn ? 'border-ochre/60' : ''}`}
      aria-label={`${sourceLabel(summary.source)} sync status`}
      data-source={summary.source}
      data-lag-warning={lag.warn ? 'true' : 'false'}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="gsl-display text-lg leading-none">{sourceLabel(summary.source)}</h3>
          <p className="mt-1 truncate text-xs text-muted-foreground" title={SOURCE_HINT[summary.source]}>
            {SOURCE_HINT[summary.source] ?? summary.source}
          </p>
        </div>
        <Chip variant={status.variant}>{status.label}</Chip>
      </header>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
        <dt className="text-xs text-muted-foreground">Last run</dt>
        <dd className="gsl-num text-right" title={run ? formatDateTime(run.startedAt) : undefined}>
          {run ? formatRelative(run.startedAt, now) : '—'}
        </dd>
        <dt className="text-xs text-muted-foreground">Took</dt>
        <dd className="gsl-num text-right">{run ? formatDuration(runDuration(run, now)) : '—'}</dd>
        <dt className="text-xs text-muted-foreground">Rows seen / changed</dt>
        <dd className="gsl-num text-right">{run ? `${num(run.rowsSeen)} / ${num(run.rowsChanged)}` : '—'}</dd>
        <dt className="text-xs text-muted-foreground">Last 24 h</dt>
        <dd className="gsl-num text-right">
          {num(summary.runsLast24h)} runs
          {summary.failuresLast24h > 0 ? <span className="text-destructive"> · {num(summary.failuresLast24h)} failed</span> : null}
        </dd>
      </dl>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <Chip variant={lagChip.variant} title={lagChip.title}>
          {lag.warn ? <Icon name="alert" size={12} className="mr-1 inline-block align-[-1px]" /> : null}
          {lagChip.label}
        </Chip>
        <span className="text-xs text-muted-foreground" title={summary.lastSuccessAt ? formatDateTime(summary.lastSuccessAt) : undefined}>
          {summary.lastSuccessAt ? `Last success ${formatRelative(summary.lastSuccessAt, now)}` : 'No success recorded'}
        </span>
      </footer>
      {run && run.errors.length > 0 ? (
        <p className="truncate rounded-lg bg-destructive/10 px-2 py-1 text-xs text-destructive" title={run.errors.join('\n')}>
          {run.errors[0]}
          {run.errors.length > 1 ? ` (+${run.errors.length - 1} more)` : ''}
        </p>
      ) : null}
    </article>
  );
}

function CountTiles({ counts }: { counts: SyncCounts }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" aria-label="Synced record counts">
      {COUNT_TILES.map((t) => (
        <div key={t.key} className="gsl-panel px-4 py-3" title={t.hint}>
          <p className="text-xs text-muted-foreground">{t.label}</p>
          <p className="gsl-display gsl-num mt-1 text-3xl leading-none" data-testid={`count-${t.key}`}>
            {num(counts[t.key])}
          </p>
        </div>
      ))}
    </div>
  );
}

function RunRow({ run, now }: { run: SyncRun; now: number }) {
  const meta = STATUS_META[run.status];
  return (
    <tr className="gsl-row border-b border-border align-top last:border-b-0">
      <td className="px-4 py-2 font-medium">{sourceLabel(run.source)}</td>
      <td className="whitespace-nowrap px-4 py-2 text-xs" title={run.startedAt}>
        <div>{formatDateTime(run.startedAt)}</div>
        <div className="text-muted-foreground">{formatRelative(run.startedAt, now)}</div>
      </td>
      <td className="gsl-num whitespace-nowrap px-4 py-2">{formatDuration(runDuration(run, now))}</td>
      <td className="px-4 py-2">
        <Chip variant={meta.variant}>{meta.label}</Chip>
      </td>
      <td className="gsl-num px-4 py-2 text-right">{num(run.rowsSeen)}</td>
      <td className="gsl-num px-4 py-2 text-right">{num(run.rowsChanged)}</td>
      <td className="px-4 py-2">
        {run.errors.length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <details className="max-w-md">
            <summary className="cursor-pointer text-xs font-medium text-destructive">
              {run.errors.length} {run.errors.length === 1 ? 'error' : 'errors'}
            </summary>
            <ul className="mt-1 space-y-1 text-xs">
              {run.errors.map((e, i) => (
                <li key={i} className="break-words rounded bg-destructive/10 px-2 py-1 font-mono">
                  {e}
                </li>
              ))}
            </ul>
          </details>
        )}
      </td>
    </tr>
  );
}

export function SyncAdmin() {
  const api = useApi();
  const query = useApiQuery((client, signal) => client.admin.sync(signal), []);
  const { reload } = query;
  const [now, setNow] = useState(() => Date.now());
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [older, setOlder] = useState<SyncRun[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState<Error | null>(null);

  // Poll while the tab is open; skip ticks while the browser tab is hidden.
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
      if (!document.hidden) reload();
    }, SYNC_POLL_MS);
    return () => window.clearInterval(id);
  }, [reload]);

  useEffect(() => {
    if (query.data) {
      const t = Date.now();
      setUpdatedAt(t);
      setNow(t);
    }
  }, [query.data]);

  const refresh = useCallback(() => {
    setNow(Date.now());
    reload();
  }, [reload]);

  const changeFilter = (source: string) => {
    setSourceFilter(source);
    setOlder([]);
    setNextBefore(null);
    setMoreError(null);
  };

  const data = query.data;
  const sources = data?.sources ?? [];
  const warnings = useMemo(() => sources.filter((s) => lagOf(s, now).warn), [sources, now]);

  const runs = useMemo(() => {
    const seen = new Set<number>();
    const merged: SyncRun[] = [];
    for (const r of [...(data?.recentRuns ?? []), ...older]) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      if (!sourceFilter || r.source === sourceFilter) merged.push(r);
    }
    return merged.sort((a, b) => b.id - a.id);
  }, [data?.recentRuns, older, sourceFilter]);

  const loadOlder = async () => {
    const before = nextBefore ?? runs[runs.length - 1]?.id;
    if (!before) return;
    setMoreBusy(true);
    setMoreError(null);
    try {
      const res = await api.admin.syncRuns({ source: sourceFilter || undefined, before, limit: RUNS_PAGE });
      setOlder((prev) => [...prev, ...res.runs]);
      setNextBefore(res.nextBefore);
    } catch (err) {
      setMoreError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setMoreBusy(false);
    }
  };

  // More runs may exist until a page comes back short.
  const canLoadOlder = runs.length > 0 && (nextBefore !== null || (older.length === 0 && (data?.recentRuns.length ?? 0) >= RUNS_PAGE));

  if (query.loading && !data) return <Spinner label="Loading sync status" />;
  if (query.error && !data) return <ErrorBanner title="Sync status could not be loaded" error={query.error} />;
  if (!data) return <EmptyState title="No sync data" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {warnings.length === 0 ? (
            <>
              <Icon name="check" size={14} className="mr-1 inline-block align-[-2px] text-pine" />
              All sources within their expected cadence.
            </>
          ) : (
            <>
              <Icon name="alert" size={14} className="mr-1 inline-block align-[-2px] text-ochre" />
              {warnings.length === 1 ? '1 source is' : `${warnings.length} sources are`} behind:{' '}
              {warnings.map((w) => sourceLabel(w.source)).join(', ')}.
            </>
          )}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{updatedAt ? `Updated ${formatRelative(new Date(updatedAt).toISOString(), now)}` : ''} · refreshes every 30 s</span>
          <Button variant="secondary" size="sm" icon="history" busy={query.loading} onClick={refresh}>
            Refresh
          </Button>
        </div>
      </div>
      {query.error ? <ErrorBanner title="The last refresh failed; showing the previous snapshot" error={query.error} /> : null}

      <section aria-label="Sync sources" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {sources.length === 0 ? (
          <div className="sm:col-span-2 xl:col-span-3">
            <EmptyState title="No sync runs recorded yet">The Functions write a row per poll cycle; the first one should appear within 15 minutes of deployment.</EmptyState>
          </div>
        ) : (
          sources.map((s) => <SourceCard key={s.source} summary={s} now={now} />)
        )}
      </section>

      <CountTiles counts={data.counts} />

      <Card
        title="Recent runs"
        padded={false}
        actions={
          <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Source</span>
            <select className="gsl-input !w-auto !py-1 text-sm" value={sourceFilter} onChange={(e) => changeFilter(e.target.value)} aria-label="Filter runs by source">
              <option value="">All</option>
              {sources.map((s) => (
                <option key={s.source} value={s.source}>
                  {sourceLabel(s.source)}
                </option>
              ))}
            </select>
          </label>
        }
      >
        {runs.length === 0 ? (
          <div className="p-4">
            <EmptyState title="No runs" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="gsl-th">Source</th>
                  <th className="gsl-th">Started</th>
                  <th className="gsl-th">Duration</th>
                  <th className="gsl-th">Status</th>
                  <th className="gsl-th text-right">Rows seen</th>
                  <th className="gsl-th text-right">Rows changed</th>
                  <th className="gsl-th">Errors</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <RunRow key={r.id} run={r} now={now} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground">
          <span>
            {runs.length} {runs.length === 1 ? 'run' : 'runs'} shown
            {sourceFilter ? ` for ${sourceLabel(sourceFilter)}` : ''}
          </span>
          {canLoadOlder ? (
            <Button variant="secondary" size="sm" busy={moreBusy} onClick={() => void loadOlder()}>
              Load older
            </Button>
          ) : null}
        </div>
        {moreError ? (
          <div className="px-4 pb-4">
            <ErrorBanner error={moreError} />
          </div>
        ) : null}
      </Card>

      {data.watermarks.length > 0 ? (
        <Card title="Watermarks" padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="gsl-th">Source</th>
                  <th className="gsl-th">Last success (UTC)</th>
                  <th className="gsl-th">Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.watermarks.map((w) => (
                  <tr key={w.source} className="gsl-row border-b border-border last:border-b-0">
                    <td className="px-4 py-2 font-medium">{sourceLabel(w.source)}</td>
                    <td className="gsl-num whitespace-nowrap px-4 py-2 text-xs" title={w.lastSuccessUtc ?? undefined}>
                      {w.lastSuccessUtc ? formatDateTime(w.lastSuccessUtc) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground" title={w.updatedAt}>
                      {formatRelative(w.updatedAt, now)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
